import sessionApi from './apiClient';

export interface Role {
  id: number;
  name: string;
  code: string;
  description: string;
  is_default: boolean;
  user_count?: number;
  permissions_display?: string[];
  is_canonical: boolean;
  permission_ids: number[];
  permissions: Permission[];
}

export interface Permission {
  id: number;
  code: string;
  name: string;
  description?: string;
}

class RoleService {
  private static roles: Role[] = [];
  private static loaded: boolean = false;

  /**
   * Load roles from backend
   */
  static async loadRoles(): Promise<Role[]> {
    try {
      const response = await sessionApi.get('/roles/');
      this.roles = response.data.results || response.data || [];
      this.loaded = true;
      return this.roles;
    } catch (error) {
      console.error('Failed to load roles:', error);
      this.roles = [];
      this.loaded = false;
      throw error;
    }
  }

  /**
   * Get all roles
   */
  static getRoles(): Role[] {
    return this.roles;
  }

  /**
   * Get role by ID
   */
  static getRoleById(id: number): Role | undefined {
    return this.roles.find(role => role.id === id);
  }

  /**
   * Get role by code
   */
  static getRoleByCode(code: string): Role | undefined {
    return this.roles.find(role => role.code === code);
  }

  /**
   * Check if roles are loaded
   */
  static isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Clear roles (useful for logout)
   */
  static clearRoles(): void {
    this.roles = [];
    this.loaded = false;
  }

  static async loadPermissions(): Promise<Permission[]> {
    const response = await sessionApi.get('/permissions/');
    return response.data.results || response.data || [];
  }

  static async updateRole(id: number, permissionIds: number[]): Promise<Role> {
    try {
      const response = await sessionApi.patch(`/roles/${id}/`, { permission_ids: permissionIds });
      return response.data;
    } catch (error) {
      console.error('Failed to update role:', error);
      throw error;
    }
  }

}

export const roleService = RoleService;
